/**
 * Counting the topics in a piece of text.
 *
 * The server counts them properly (it knows what the formatter turned into a
 * topic), but the composer needs an answer before the post is sent, so it
 * counts the words that look like topics.
 *
 * The rule is deliberately crude: a "#" followed by at least two characters
 * that are not whitespace. It will count a "#" inside a URL as a topic, and
 * it will not count a topic that is only one character long — both are
 * acceptable, because the number is only used to show "3 / 5 topics used" and
 * to stop the suggestions when the quota is used up. The server has the last
 * word either way.
 */
const TOPIC_PATTERN = /#([^\s#]{2,})/g;

/**
 * How many topics the text carries.
 */
export function countTopics(text) {
  if (!text) return 0;
  const matches = text.match(TOPIC_PATTERN);
  return matches ? matches.length : 0;
}

/**
 * The names of the topics in the text, as written.
 */
export function topicsInText(text) {
  if (!text) return [];
  const names = [];
  TOPIC_PATTERN.lastIndex = 0;
  let match;
  while ((match = TOPIC_PATTERN.exec(text)) !== null) {
    names.push(match[1]);
  }
  return names;
}

/**
 * How many topics the text has "used up" for the per-post quota.
 *
 * Same count as countTopics(), except that a word at the very end of the
 * text is left out: the composer puts a space after a topic picked from the
 * dropdown, so a finished topic is followed by whitespace, while a word that
 * ends the text is still being typed. Counting it would hide the suggestions
 * at exactly the moment the user is typing their next topic — with four
 * topics already written, the fifth would never be offered.
 *
 * The server has the final word; this number only drives the counter and the
 * "no more suggestions" switch.
 */
export function usedTopicCount(text) {
  if (!text) return 0;
  const matches = text.match(TOPIC_PATTERN);
  if (!matches || !matches.length) return 0;
  if (/\s$/.test(text)) return matches.length;
  const last = matches[matches.length - 1];
  const lastIndex = text.lastIndexOf(last);
  return text.slice(lastIndex + last.length).trim() === '' ? matches.length - 1 : matches.length;
}
flarum.reg.add('ziiven-topics', 'forum/utils/topicUsage', { countTopics: countTopics,topicsInText: topicsInText,usedTopicCount: usedTopicCount, });